import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FormInput,
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Eye,
  EyeOff,
  AlertCircle,
  Save,
  RotateCcw,
  Building2,
  User,
  CreditCard
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Campos por defecto del onboarding
const DEFAULT_FIELDS = {
  empresa: [
    { key: 'razon_social', label: 'Razón Social', tipo: 'text', obligatorio: true, visible: true, editable: false },
    { key: 'cuit', label: 'CUIT', tipo: 'text', obligatorio: true, visible: true, editable: false },
    { key: 'domicilio', label: 'Domicilio', tipo: 'text', obligatorio: true, visible: true, editable: true },
    { key: 'localidad', label: 'Localidad', tipo: 'text', obligatorio: true, visible: true, editable: true },
    { key: 'provincia', label: 'Provincia', tipo: 'select', obligatorio: true, visible: true, editable: true },
    { key: 'codigo_postal', label: 'Código Postal', tipo: 'text', obligatorio: false, visible: true, editable: true },
    { key: 'telefono', label: 'Teléfono', tipo: 'text', obligatorio: false, visible: true, editable: true },
    { key: 'email', label: 'Email de la Empresa', tipo: 'email', obligatorio: false, visible: true, editable: true },
    { key: 'actividad_principal', label: 'Actividad Principal', tipo: 'text', obligatorio: false, visible: true, editable: true },
    { key: 'fecha_constitucion', label: 'Fecha de Constitución', tipo: 'date', obligatorio: false, visible: true, editable: true },
  ],
  limites: [
    { key: 'limite_diario', label: 'Límite Diario Solicitado', tipo: 'number', obligatorio: false, visible: true, editable: true },
    { key: 'limite_mensual', label: 'Límite Mensual Solicitado', tipo: 'number', obligatorio: false, visible: true, editable: true },
    { key: 'justificacion_limites', label: 'Justificación de Límites', tipo: 'textarea', obligatorio: false, visible: true, editable: true },
  ]
};

const TIPOS_CAMPO = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Selector' },
  { value: 'textarea', label: 'Texto Largo' },
];

const SECCIONES = [
  { key: 'empresa', label: 'Datos de la Empresa', icon: Building2, color: 'text-blue-600' },
  { key: 'limites', label: 'Límites Operativos', icon: CreditCard, color: 'text-green-600' },
];

const OnboardingFieldsConfig = () => {
  const [config, setConfig] = useState({ empresa: [], limites: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('empresa');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  
  const [newField, setNewField] = useState({
    key: '',
    label: '',
    tipo: 'text',
    obligatorio: false,
    visible: true,
    placeholder: '',
    opciones: ''
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/onboarding-config/fields');
      if (response.data && (response.data.empresa?.length > 0 || response.data.limites?.length > 0)) {
        setConfig(response.data);
      } else {
        // Usar configuración por defecto
        setConfig(DEFAULT_FIELDS);
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setConfig(DEFAULT_FIELDS);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/onboarding-config/fields', config);
      toast.success('Configuración guardada');
      setHasChanges(false);
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('¿Restablecer a la configuración por defecto? Se perderán los cambios personalizados.')) return;
    
    try {
      await api.post('/onboarding-config/fields/reset');
      setConfig(DEFAULT_FIELDS);
      setHasChanges(false);
      toast.success('Configuración restablecida');
    } catch (error) {
      toast.error('Error restableciendo');
    }
  };

  const handleToggleVisible = (section, index) => {
    const newConfig = { ...config };
    const field = newConfig[section][index];
    
    // No permitir ocultar campos obligatorios del sistema
    if (field.editable === false && field.obligatorio) {
      toast.error('Este campo es requerido por el sistema');
      return;
    }
    
    newConfig[section][index].visible = !field.visible;
    if (!newConfig[section][index].visible) {
      newConfig[section][index].obligatorio = false;
    }
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleToggleObligatorio = (section, index) => {
    const newConfig = { ...config };
    const field = newConfig[section][index];
    
    // No permitir cambiar obligatoriedad de campos del sistema
    if (field.editable === false) {
      toast.error('Este campo no puede modificarse');
      return;
    }
    
    newConfig[section][index].obligatorio = !field.obligatorio;
    if (newConfig[section][index].obligatorio) {
      newConfig[section][index].visible = true;
    }
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newConfig = { ...config };
    const items = Array.from(newConfig[activeSection]);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    newConfig[activeSection] = items.map((item, idx) => ({ ...item, orden: idx + 1 }));
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleAddField = () => {
    if (!newField.key.trim() || !newField.label.trim()) {
      toast.error('Key y Label son requeridos');
      return;
    }
    
    // Verificar que el key no exista
    const exists = config[activeSection].some(f => f.key === newField.key);
    if (exists) {
      toast.error('Ya existe un campo con esa clave');
      return;
    }
    
    const newConfig = { ...config };
    newConfig[activeSection].push({
      ...newField,
      editable: true,
      orden: newConfig[activeSection].length + 1,
      opciones: newField.tipo === 'select' ? newField.opciones.split(',').map(o => o.trim()).filter(Boolean) : undefined
    });
    
    setConfig(newConfig);
    setHasChanges(true);
    setShowAddDialog(false);
    setNewField({ key: '', label: '', tipo: 'text', obligatorio: false, visible: true, placeholder: '', opciones: '' });
    toast.success('Campo agregado');
  };

  const handleDeleteField = (section, index) => {
    const field = config[section][index];
    
    if (field.editable === false) {
      toast.error('Este campo no puede eliminarse');
      return;
    }
    
    if (!confirm(`¿Eliminar el campo "${field.label}"?`)) return;
    
    const newConfig = { ...config };
    newConfig[section].splice(index, 1);
    setConfig(newConfig);
    setHasChanges(true);
    toast.success('Campo eliminado');
  };

  const openEditDialog = (section, index) => {
    const field = config[section][index];
    setSelectedField({ section, index, ...field });
    setShowEditDialog(true);
  };

  const handleUpdateField = () => {
    if (!selectedField) return;
    
    const newConfig = { ...config };
    newConfig[selectedField.section][selectedField.index] = {
      ...newConfig[selectedField.section][selectedField.index],
      label: selectedField.label,
      placeholder: selectedField.placeholder,
      opciones: selectedField.tipo === 'select' && selectedField.opciones 
        ? (typeof selectedField.opciones === 'string' 
            ? selectedField.opciones.split(',').map(o => o.trim()).filter(Boolean) 
            : selectedField.opciones)
        : undefined
    };
    
    setConfig(newConfig);
    setHasChanges(true);
    setShowEditDialog(false);
    setSelectedField(null);
    toast.success('Campo actualizado');
  };

  const currentFields = config[activeSection] || [];
  const visibleCount = currentFields.filter(f => f.visible).length;
  const obligatoriosCount = currentFields.filter(f => f.obligatorio).length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <FormInput className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="onboarding-fields-config">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FormInput className="text-purple-600" />
            Campos del Onboarding
          </h1>
          <p className="text-slate-600 mt-1">
            Configura qué datos se solicitan durante el registro de nuevas empresas
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw size={16} className="mr-2" />
            Restablecer
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || saving}
            className={hasChanges ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-600" />
          <span className="text-sm text-amber-700">Tienes cambios sin guardar</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Secciones */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-3">Sección</h3>
            <div className="space-y-2">
              {SECCIONES.map(seccion => {
                const Icon = seccion.icon;
                const isSelected = activeSection === seccion.key;
                const sectionFields = config[seccion.key] || [];
                
                return (
                  <button
                    key={seccion.key}
                    onClick={() => setActiveSection(seccion.key)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isSelected 
                        ? 'bg-purple-50 border-2 border-purple-300' 
                        : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                    }`}
                  >
                    <Icon size={20} className={seccion.color} />
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isSelected ? 'text-purple-700' : 'text-slate-700'}`}>
                        {seccion.label}
                      </p>
                      <p className="text-xs text-slate-500">
                        {sectionFields.filter(f => f.visible).length} campos visibles
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-semibold text-sm mb-3">Resumen</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Campos visibles</span>
                <span className="font-medium text-green-600">{visibleCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Obligatorios</span>
                <span className="font-medium text-amber-600">{obligatoriosCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Total</span>
                <span className="font-medium">{currentFields.length}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Contenido - Lista de Campos */}
        <div className="lg:col-span-3">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {SECCIONES.find(s => s.key === activeSection)?.label}
              </h2>
              <Button size="sm" onClick={() => setShowAddDialog(true)}>
                <Plus size={14} className="mr-1" />
                Agregar Campo
              </Button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="fields">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                    {currentFields.map((field, index) => (
                      <Draggable key={field.key} draggableId={field.key} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                              snapshot.isDragging 
                                ? 'bg-purple-50 border-purple-300 shadow-lg' 
                                : field.visible 
                                  ? 'bg-white border-slate-200 hover:border-slate-300' 
                                  : 'bg-slate-50 border-slate-200 opacity-60'
                            }`}
                          >
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
                            >
                              <GripVertical size={18} className="text-slate-400" />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{field.label}</span>
                                {field.obligatorio && (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">Obligatorio</Badge>
                                )}
                                {field.editable === false && (
                                  <Badge variant="secondary" className="text-xs">Sistema</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="text-xs text-slate-500 bg-slate-100 px-1 rounded">{field.key}</code>
                                <span className="text-xs text-slate-400">•</span>
                                <span className="text-xs text-slate-500">{TIPOS_CAMPO.find(t => t.value === field.tipo)?.label || field.tipo}</span>
                              </div>
                            </div>

                            {/* Controles */}
                            <div className="flex items-center gap-2">
                              {/* Toggle Obligatorio */}
                              <button
                                onClick={() => handleToggleObligatorio(activeSection, index)}
                                disabled={field.editable === false}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  field.obligatorio 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                } ${field.editable === false ? 'cursor-not-allowed opacity-50' : ''}`}
                                title="Marcar como obligatorio"
                              >
                                {field.obligatorio ? 'Obligatorio' : 'Opcional'}
                              </button>

                              {/* Toggle Visible */}
                              <button
                                onClick={() => handleToggleVisible(activeSection, index)}
                                disabled={field.editable === false && field.obligatorio}
                                className={`p-2 rounded-lg transition-colors ${
                                  field.visible 
                                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                } ${field.editable === false && field.obligatorio ? 'cursor-not-allowed opacity-50' : ''}`}
                                title={field.visible ? 'Ocultar' : 'Mostrar'}
                              >
                                {field.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                              </button>

                              {/* Editar */}
                              <button
                                onClick={() => openEditDialog(activeSection, index)}
                                className="p-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>

                              {/* Eliminar */}
                              {field.editable !== false && (
                                <button
                                  onClick={() => handleDeleteField(activeSection, index)}
                                  className="p-2 rounded-lg bg-slate-100 text-red-500 hover:bg-red-50 transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {currentFields.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <FormInput size={40} className="mx-auto mb-2 opacity-50" />
                <p>No hay campos configurados</p>
              </div>
            )}

            {/* Leyenda */}
            <div className="mt-6 pt-4 border-t flex flex-wrap gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Badge className="bg-amber-100 text-amber-700 text-xs">Obligatorio</Badge>
                <span>Campo requerido</span>
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="text-xs">Sistema</Badge>
                <span>No modificable</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye size={14} className="text-green-600" />
                <span>Visible</span>
              </div>
              <div className="flex items-center gap-1">
                <EyeOff size={14} className="text-slate-400" />
                <span>Oculto</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Dialog Agregar Campo */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="text-purple-600" />
              Agregar Campo Personalizado
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clave (key) *</Label>
                <Input
                  placeholder="nombre_campo"
                  value={newField.key}
                  onChange={e => setNewField(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                />
                <p className="text-xs text-slate-500 mt-1">Identificador único</p>
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select
                  value={newField.tipo}
                  onValueChange={v => setNewField(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CAMPO.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Etiqueta *</Label>
              <Input
                placeholder="Nombre del Campo"
                value={newField.label}
                onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div>
              <Label>Placeholder</Label>
              <Input
                placeholder="Texto de ayuda..."
                value={newField.placeholder}
                onChange={e => setNewField(prev => ({ ...prev, placeholder: e.target.value }))}
              />
            </div>

            {newField.tipo === 'select' && (
              <div>
                <Label>Opciones (separadas por coma)</Label>
                <Input
                  placeholder="Opción 1, Opción 2, Opción 3"
                  value={newField.opciones}
                  onChange={e => setNewField(prev => ({ ...prev, opciones: e.target.value }))}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label>Campo Obligatorio</Label>
              <Switch
                checked={newField.obligatorio}
                onCheckedChange={v => setNewField(prev => ({ ...prev, obligatorio: v, visible: v ? true : prev.visible }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddField} disabled={!newField.key || !newField.label}>
              Agregar Campo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Campo */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" />
              Editar Campo
            </DialogTitle>
          </DialogHeader>
          
          {selectedField && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Clave (key)</Label>
                <Input value={selectedField.key} disabled className="bg-slate-50" />
              </div>

              <div>
                <Label>Etiqueta *</Label>
                <Input
                  value={selectedField.label}
                  onChange={e => setSelectedField(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>

              <div>
                <Label>Placeholder</Label>
                <Input
                  value={selectedField.placeholder || ''}
                  onChange={e => setSelectedField(prev => ({ ...prev, placeholder: e.target.value }))}
                />
              </div>

              {selectedField.tipo === 'select' && (
                <div>
                  <Label>Opciones (separadas por coma)</Label>
                  <Input
                    value={Array.isArray(selectedField.opciones) ? selectedField.opciones.join(', ') : selectedField.opciones || ''}
                    onChange={e => setSelectedField(prev => ({ ...prev, opciones: e.target.value }))}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateField}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingFieldsConfig;
